// xG Table — sortable expected-goals table (Phase 3). Its own component (not
// StandingsTable): different columns, coloured signed "Diff" cells with
// `data-val`, and the xGD `col-diff` cell. Reuses the shared sort core
// (useSortableTable) and the FLIP hook (useFlipRows). Emits the exact
// post-initSortableTable DOM so the shared CSS renders it identically.

import { useMemo, useRef } from "react";
import { shapeXgRows, XG_COLUMNS, XG_DEFAULT_RATE, XG_DEFAULT_TIMELINE, type XgRow } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import { useSortableTable } from "../../hooks/useSortableTable.js";
import { useFlipRows } from "../../hooks/useFlipRows.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface XgTableProps {
  fixture: string;
  /** Rate toggle: "Per Game" | "Per Season". */
  rate?: string | null;
  /** Timeline toggle: "5" | "10" | "Season". */
  timeline?: string | null;
}

// Sortable value per column — team label for text; for the coloured Diff cells
// and xGD the raw signed `data-val` (parsed by the sorter), matching how the
// legacy sort read `dataset.val`.
function cellValue(row: XgRow, key: string): string | number {
  switch (key) {
    case "team":
      return row.label;
    case "p":
      return row.p;
    case "xgf":
      return row.xgf;
    case "xgfDiff":
      return row.xgfDiff.dataVal;
    case "xga":
      return row.xga;
    case "xgaDiff":
      return row.xgaDiff.dataVal;
    case "xgd":
      return row.xgd.dataVal;
    case "xgdDiff":
      return row.xgdDiff.dataVal;
    case "xpts":
      return row.xpts;
    default:
      return "";
  }
}

export default function XgTable({ fixture, rate, timeline }: XgTableProps) {
  const rateVal = rate ?? XG_DEFAULT_RATE;
  const tlVal = timeline ?? XG_DEFAULT_TIMELINE;
  const { data, isPending, isError, refetch } = useWidgetData(
    "xg-table",
    useFixtureKey(fixture)
  );
  // Full dataset keyed by `${rate}-${timeline}`; pick the active window
  // client-side (same lookup the embedded selector did) so toggles are instant.
  const teams =
    data?.[`${rateVal}-${tlVal}`] ??
    data?.[`${XG_DEFAULT_RATE}-${XG_DEFAULT_TIMELINE}`] ??
    [];

  const mobile = window.innerWidth <= 768;
  const base = useMemo(
    () => shapeXgRows(teams, fixture, mobile),
    [teams, fixture, mobile]
  );

  const { rows, sort, toggle, reset } = useSortableTable(
    base,
    XG_COLUMNS,
    cellValue,
    `${rateVal}|${tlVal}|${fixture}`
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  useFlipRows(
    wrapperRef,
    rows.map((r) => r.name).join(",")
  );

  if (isPending) return <WidgetSkeleton rows={6} />;
  if (isError) return <WidgetError onRetry={refetch} />;

  return (
    <div className="data-wrapper top-align" ref={wrapperRef}>
      <div className="data-row header">
        <div className="col-rank" onClick={reset}>
          #
        </div>
        {XG_COLUMNS.map((col, i) => {
          const active = sort.key === col.key;
          const indicator = active ? (sort.asc ? " sort-asc" : " sort-desc") : "";
          return (
            <div
              key={`${col.key}-${i}`}
              className={`${col.cls} sortable${indicator}`}
              onClick={() => toggle(col)}
            >
              {col.label}
            </div>
          );
        })}
      </div>

      {rows.map((r, i) => (
        <div
          key={r.name}
          data-flip-key={r.name}
          className={`data-row${r.highlighted ? " highlighted" : ""}`}
        >
          <div className="col-rank">{i + 1}</div>
          <div className="col-team">{r.label}</div>
          <div className="col-num col-opt">{r.p}</div>
          <div className="col-num col-opt">{r.xgf}</div>
          <div
            className={`col-num col-delta ${r.xgfDiff.cls}`}
            data-val={r.xgfDiff.dataVal}
          >
            {r.xgfDiff.text}
          </div>
          <div className="col-num col-opt">{r.xga}</div>
          <div
            className={`col-num col-delta ${r.xgaDiff.cls}`}
            data-val={r.xgaDiff.dataVal}
          >
            {r.xgaDiff.text}
          </div>
          <div className="col-num col-diff" data-val={r.xgd.dataVal}>
            {r.xgd.text}
          </div>
          <div
            className={`col-num col-delta ${r.xgdDiff.cls}`}
            data-val={r.xgdDiff.dataVal}
          >
            {r.xgdDiff.text}
          </div>
          <div className="col-num">{r.xpts}</div>
        </div>
      ))}
    </div>
  );
}
